import React, { useState } from 'react';
import {
  Box,
  Image,
  Text,
  Button,
  HStack,
  IconButton,
  Tooltip,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { TimeIcon, LinkIcon, EditIcon } from '@chakra-ui/icons';
import { MdLocationOn } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { mutateFunction } from '../../libs/apis';
import { CustomToast } from '../toast/CustomToast';
import RSVPMenuDropDown from './RSVPMenuDropDown';

const EventCard = ({ event, tabIndex, refetchEvents, refetchMyEvents }) => {
  const userDetails = useSelector((state) => state?.user?.userInfo);
  const [linkStatus, setLinkStatus] = useState(
    event?.event_link ? 'Edit' : 'Add',
  );

  const statusMap = {
    attending: 'Going',
    interested: 'Interested',
    not_attending: 'Not interested',
  };

  const [status, setStatus] = useState(
    statusMap[
    event?.rsvps?.find((rsvp) => rsvp?.user_id === userDetails?.id)?.rsvp_type
    ],
  );
  const [eventLink, setEventLink] = useState(event?.event_link || '');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isImageModalOpen,
    onOpen: onImageModalOpen,
    onClose: onImageModalClose,
  } = useDisclosure();
  const { addToast } = CustomToast();

  const { mutate: updateEventLink, isLoading: isUpdating } =
    useMutation(mutateFunction);
  const { mutate: updateCoverImage, isLoading: isUpdatingImage } =
    useMutation(mutateFunction);

  const [selectedImage, setSelectedImage] = useState(null);

  const handleUpdateEventLink = () => {
    updateEventLink(
      {
        key: `events/${event.id}`,
        method: 'put',
        data: { event_link: eventLink },
      },
      {
        onSuccess: (res) => {
          addToast(res);
          if (tabIndex === 0) {
            refetchEvents();
          } else {
            refetchMyEvents();
          }
          onClose();
        },
        onError: (error) => {
          addToast({ message: 'Error updating event link' }, 'error');
        },
      },
    );
  };

  const handleImageUpdate = () => {
    if (!selectedImage) return;

    const formData = new FormData();
    formData.append('cover_image', selectedImage);

    updateCoverImage(
      {
        key: `events/${event.id}`,
        method: 'put',
        data: formData,
      },
      {
        onSuccess: (res) => {
          addToast(res);
          if (tabIndex === 0) {
            refetchEvents();
          } else {
            refetchMyEvents();
          }
          setSelectedImage(null);
          onImageModalClose();
        },
        onError: (error) => {
          addToast({ message: 'Error updating cover image' }, 'error');
        },
      },
    );
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Add function to determine event status
  const getEventStatus = () => {
    const now = new Date();
    const startDate = new Date(event?.start_date);
    const eventDate = new Date(event?.end_date);

    if (startDate <= now && eventDate >= now) {
      return { status: 'ongoing', color: 'yellow.500', text: 'Ongoing Event' };
    } else if (eventDate > now) {
      return { status: 'upcoming', color: 'green.500', text: 'Upcoming Event' };
    } else {
      return { status: 'past', color: 'gray.500', text: 'Past Event' };
    }
  };

  const eventStatus = getEventStatus();


  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      overflow="hidden"
      _hover={{ boxShadow: 'xl' }}
      transition="box-shadow 0.2s"
      h="450px"
      display="flex"
      flexDirection="column"
    >
      <Box
        bg={event?.cover_image ? 'transparent' : 'blue.100'}
        h="160px"
        w="100%"
        position="relative"
      >
        {event?.cover_image && (
          <Image
            src={event.cover_image}
            alt={event?.name || 'Event cover image'}
            objectFit="cover"
            w="100%"
            h="100%"
            position="absolute"
            top="0"
            left="0"
          />
        )}

        {/* Event Status Badge */}
        <Box
          position="absolute"
          top="2"
          left="2"
          bg={eventStatus.color}
          color="white"
          px={2}
          py={1}
          borderRadius="md"
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
        >
          {eventStatus.text}
        </Box>

        {event?.user_id === userDetails?.id && (
          <Tooltip label="Update cover image">
            <IconButton
              aria-label="Update cover image"
              icon={<EditIcon />}
              size="sm"
              position="absolute"
              top="2"
              right="2"
              bg="white"
              _hover={{ bg: 'gray.100' }}
              onClick={(e) => {
                e.stopPropagation();
                onImageModalOpen();
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box p={4} flex="1" display="flex" flexDirection="column">
        <Text fontWeight="bold" fontSize="lg" mb={1}>
          {event.name}
        </Text>
        <HStack spacing={2} mb={2}>
          <TimeIcon color="gray.500" />
          <Text fontSize="sm" color="gray.500">
            {new Date(event?.start_date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}{' '}
            &bull;{' '}
            {new Date(event?.start_date).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </HStack>
        <HStack spacing={2} mb={2}>
          <MdLocationOn />
          <Text fontSize="sm" color="gray.600">
            {event?.location}
          </Text>
        </HStack>

        {/* RSVP Counts Section */}
        <HStack spacing={2} mb={2}>
          <Text fontSize="sm" color="gray.700">
            {event?.rsvp_counts?.interested || 0} interested ·{' '}
            {event?.rsvp_counts?.attending || 0} going
          </Text>
        </HStack>

        {/* Event Link Section */}
        {event?.event_link ? (
          <HStack spacing={2} mb={3}>
            <LinkIcon color="blue.500" />
            <Text fontSize="sm" color="blue.500" noOfLines={1}>
              <a
                href={event.event_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {event.event_link}
              </a>
            </Text>

            {event?.user_id === userDetails?.id && (
              <Tooltip label="Edit event link">
                <IconButton
                  aria-label="Edit event link"
                  icon={<LinkIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkStatus('Edit');
                    onOpen();
                  }}
                />
              </Tooltip>
            )}
          </HStack>
        ) : event?.user_id === userDetails?.id ? (
          <Button
            size="sm"
            leftIcon={<LinkIcon />}
            variant="ghost"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation();
              setLinkStatus('Add');
              onOpen();
            }}
            mb={3}
            _hover={{ bg: 'transparent' }}
            justifyContent="flex-start"
            w="fit-content"
          >
            Add Event Link
          </Button>
        ) : null}

        <Text
          fontSize="sm"
          noOfLines={2}
          mb={3}
          textOverflow="ellipsis"
          className="event_card"
          flex="1"
        >
          {event?.description}
        </Text>

        {new Date(event.end_date) >= new Date() && (
          <RSVPMenuDropDown
            status={status}
            setStatus={setStatus}
            eventId={event?.id}
            tabIndex={tabIndex}
            refetchEvents={refetchEvents}
            refetchMyEvents={refetchMyEvents}
          />
        )}
      </Box>

      {/* Edit Event Link Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          w={{ base: '90%', md: '500px' }}
          maxW={{ base: '90%', md: '500px' }}
        >
          <ModalHeader>
            {linkStatus === 'Edit' ? 'Edit Event Link' : 'Add Event Link'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Event Link</FormLabel>
              <Input
                value={eventLink}
                onChange={(e) => setEventLink(e.target.value)}
                placeholder="Enter event link"
              />
            </FormControl>
            <Button
              mt={4}
              colorScheme="blue"
              onClick={handleUpdateEventLink}
              isLoading={isUpdating}
              isDisabled={!isValidUrl(eventLink)}
            >
              {linkStatus === 'Edit' ? 'Update Link' : 'Add Link'}
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Update Cover Image Modal */}
      <Modal isOpen={isImageModalOpen} onClose={onImageModalClose}>
        <ModalOverlay />
        <ModalContent
          w={{ base: '90%', md: '500px' }}
          maxW={{ base: '90%', md: '500px' }}
        >
          <ModalHeader>Update Cover Image</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Select New Cover Image</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                disabled={isUpdatingImage}
              />
              {selectedImage && (
                <Box mt={4}>
                  <Image
                    src={URL.createObjectURL(selectedImage)}
                    alt="Selected cover image"
                    maxH="200px"
                    objectFit="contain"
                  />
                </Box>
              )}
            </FormControl>
            <Button
              mt={4}
              colorScheme="blue"
              onClick={handleImageUpdate}
              isLoading={isUpdatingImage}
              isDisabled={!selectedImage || isUpdatingImage}
              w="full"
            >
              Update Cover Image
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EventCard;
